import { useEffect, useState } from "react";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import { ApiError } from "../../Common/ApiError";
import { getDateRange } from "../../../../utils/DateUtils";
import LoadingCenter from "../../Common/LoadingCenter";
import { useTranslation } from "react-i18next";
import { convertDoubleToStringPTBR } from "../../../../utils/ConverterUtils";
import { Alert, Col, FormControl, FormGroup, FormLabel, Row } from "react-bootstrap";
import { successAlert, warnAlert } from "../../alert/defaultAlert";
import ModalPageEvent from "../../Common/Modal/ModalPageEvent";
import InputMoney from "../../Forms/Element/InputMoney";
import TextArea from "../../Forms/Element/TextArea";

const DistributionPot = (props) => {

    const { t } = useTranslation();
    // onAdjustmentChange: o ajuste de valor ou de pontuação muda o que os cards e os
    // gráficos da tela mostram, e sem avisar o pai o operador precisava clicar em Pesquisar
    // de novo para ver o efeito do que acabou de salvar.
    const { month, year, branchId, onAdjustmentChange, distribution } = props;
    const [data, setData] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const [allowPotValueAdjustment, setAllowPotValueAdjustment] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [adjustedValue, setAdjustedValue] = useState(null);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    // Correção manual da pontuação por colaborador (mesma tela do pote).
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [scoreEmployee, setScoreEmployee] = useState(null);
    const [adjustedPoints, setAdjustedPoints] = useState("");
    const [scoreReason, setScoreReason] = useState("");
    const [savingScore, setSavingScore] = useState(false);

    // Quando recebe a resposta consolidada, o pai é a fonte de verdade e recarrega rateio,
    // gráficos e cards juntos. O modo local continua disponível para reutilizações antigas.
    const refreshAfterAdjustment = () => {
        if (onAdjustmentChange) {
            onAdjustmentChange();
        } else {
            fetchData();
        }
    };

    const fetchData = () => {
        const dateRange = getDateRange(month, year);
        setShowLoading(true);
        axiosInstance.get(`${Api.gamification.pot}/interval/${dateRange.start}/${dateRange.end}`, {
            params: { branchId }
        })
            .then(resp => setData(resp.data))
            .catch(error => { setData(null); ApiError(error); })
            .finally(() => setShowLoading(false));
    };

    useEffect(() => {
        if (distribution) {
            setData(distribution);
            setShowLoading(false);
            return;
        }
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month, year, branchId, distribution]);

    useEffect(() => {
        axiosInstance.get(Api.parameter.gamification)
            .then(({ data }) => {
                // O endpoint de parâmetros normalmente retorna uma lista, mas aceitar também
                // um objeto evita que o botão volte a aparecer quando a API retorna o registro
                // único em uma resposta simplificada.
                const parameter = Array.isArray(data) ? data[0] : data;
                setAllowPotValueAdjustment(parameter?.allowPotValueAdjustment !== false);
            })
            .catch(() => setAllowPotValueAdjustment(true));
    }, []);

    const hasDistribution = (data?.distribution?.length || 0) > 0;
    const hasAdjustment = !!data?.adjustmentId;
    // Acordos individuais do período. A coluna e o card só aparecem quando existe algum:
    // numa barbearia sem acordo nenhum eles seriam uma coluna de traços e um card zerado.
    const bonusTotal = Number(data?.bonusTotal || 0);
    const hasBonus = bonusTotal > 0;

    const openModal = () => {
        // Pré-preenche com o valor atual do pote (ajustado ou original)
        setAdjustedValue(data?.potTotal ?? null);
        setReason("");
        setShowModal(true);
    };

    const handleSaveAdjustment = () => {
        if (adjustedValue == null || adjustedValue < 0) {
            warnAlert(t('validation.required.value'));
            return;
        }
        setSaving(true);
        axiosInstance.post(Api.gamification.potAdjustment, {
            month,
            year,
            // Valor original que estava sendo calculado antes do ajuste
            originalValue: hasAdjustment ? data.potTotalOriginal : data.potTotal,
            adjustedValue,
            reason: reason || null
        })
            .then(() => {
                setShowModal(false);
                successAlert(t('gamification.pot_adjustment_saved'));
                refreshAfterAdjustment();
            })
            .catch(ApiError)
            .finally(() => setSaving(false));
    };

    const handleRemoveAdjustment = () => {
        axiosInstance.delete(`${Api.gamification.potAdjustment}/${data.adjustmentId}`)
            .then(() => {
                successAlert(t('gamification.pot_adjustment_removed'));
                refreshAfterAdjustment();
            })
            .catch(ApiError);
    };

    const openScoreModal = (item) => {
        // Pré-preenche com a pontuação atual (ajustada ou calculada) do colaborador.
        setScoreEmployee(item);
        setAdjustedPoints(item.points ?? 0);
        setScoreReason("");
        setShowScoreModal(true);
    };

    const handleSaveScoreAdjustment = () => {
        const points = parseInt(adjustedPoints, 10);
        if (isNaN(points) || points < 0) {
            warnAlert(t('validation.required.value'));
            return;
        }
        setSavingScore(true);
        axiosInstance.post(Api.gamification.scoreAdjustment, {
            employeeId: scoreEmployee.employeeId,
            month,
            year,
            // Pontuação calculada automaticamente antes de qualquer correção.
            originalPoints: scoreEmployee.originalPoints,
            adjustedPoints: points,
            reason: scoreReason || null
        })
            .then(() => {
                setShowScoreModal(false);
                successAlert(t('gamification.score_adjustment_saved'));
                refreshAfterAdjustment();
            })
            .catch(ApiError)
            .finally(() => setSavingScore(false));
    };

    const handleRemoveScoreAdjustment = (item) => {
        axiosInstance.delete(`${Api.gamification.scoreAdjustment}/${item.scoreAdjustmentId}`)
            .then(() => {
                successAlert(t('gamification.score_adjustment_removed'));
                refreshAfterAdjustment();
            })
            .catch(ApiError);
    };

    return (
        <>
            <div className="pot-dashboard__summary-panel">
                {showLoading ? (
                    <>
                        <LoadingCenter />
                        <p className="text-center mt-3 mb-0 pot-dashboard__loading-copy">
                            {t('gamification.message.wait_calculate_commission_pot')}!
                        </p>
                    </>
                ) : (
                    <>
                        <div className="pot-dashboard__summary-head">
                            <div>
                                <h5>{t('gamification.pot_summary_title')}</h5>
                                <p>{t('gamification.pot_summary_hint')}</p>
                            </div>
                            <div className="pot-dashboard__formula">
                                <strong>{t('gamification.pot_formula_title')}</strong>
                                <span>{t('gamification.pot_formula_hint')}</span>
                            </div>
                        </div>

                        <div className="pot-dashboard__summary-grid">
                            <div className="pot-dashboard__summary-card">
                                <span>{t('total_subscription')}</span>
                                {allowPotValueAdjustment && hasAdjustment && (
                                    <small className="text-muted text-decoration-line-through d-block">
                                        {t('currency_symbol')} {convertDoubleToStringPTBR(data.potTotalOriginal || 0)}
                                    </small>
                                )}
                                <strong className={allowPotValueAdjustment && hasAdjustment ? "text-warning" : ""}>
                                    {t('currency_symbol')} {convertDoubleToStringPTBR(data?.potTotal || 0)}
                                    {allowPotValueAdjustment && hasAdjustment && (
                                        <span className="ms-1" title={t('gamification.pot_adjusted_value')}>
                                            <i className="fas fa-pen-to-square fa-xs"></i>
                                        </span>
                                    )}
                                </strong>
                                {/* O ajuste é da empresa inteira no mês. Com filial selecionada a tela mostra
                                    a parte dela do valor ajustado, e ajustar daqui gravaria o valor da filial
                                    como se fosse o da empresa: por isso só em "Todas as filiais". */}
                                {allowPotValueAdjustment && branchId && (
                                    <small className="text-muted d-block mt-1">
                                        {hasAdjustment
                                            ? t('gamification.pot_adjustment_branch_share')
                                            : t('gamification.pot_adjustment_all_branches_only')}
                                    </small>
                                )}
                                <div className="d-flex gap-1 mt-1">
                                    {allowPotValueAdjustment && !branchId && <button
                                        className="btn btn-link btn-sm p-0 text-primary"
                                        onClick={openModal}
                                        title={t('gamification.pot_adjust_value')}
                                    >
                                        <i className="fas fa-sliders me-1"></i>
                                        {hasAdjustment ? t('gamification.pot_edit_adjustment') : t('gamification.pot_adjust_value')}
                                    </button>}
                                    {allowPotValueAdjustment && !branchId && hasAdjustment && (
                                        <button
                                            className="btn btn-link btn-sm p-0 text-danger ms-2"
                                            onClick={handleRemoveAdjustment}
                                            title={t('gamification.pot_remove_adjustment')}
                                        >
                                            <i className="fas fa-rotate-left me-1"></i>
                                            {t('gamification.pot_remove_adjustment')}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="pot-dashboard__summary-card">
                                {/* potCommission é o percentual do pote que vai para os COLABORADORES
                                    (PotService: employeeCommission = potTotal * potCommission/100).
                                    O rótulo dizia "para a Empresa" e ficava logo acima de "Comissão da
                                    Empresa", que exibe os 60% restantes - a tela parecia se contradizer. */}
                                <span>{t('gamification.employee_pot_commission')}</span>
                                <strong>{convertDoubleToStringPTBR(data?.potCommission || 0)}%</strong>
                            </div>
                            <div className="pot-dashboard__summary-card">
                                {/* Com acordo individual no período, este valor já vem
                                    líquido: é o que sobrou para a casa depois de honrar os
                                    acordos. Sem dizer isso, a empresa parece ter perdido
                                    dinheiro sem explicação de um mês para o outro. */}
                                <span>{hasBonus ? t('pot_bonus_company_share') : t('company_commission')}</span>
                                <strong>{t('currency_symbol')} {convertDoubleToStringPTBR(data?.companyCommission || 0)}</strong>
                            </div>
                            {hasBonus && (
                                <div className="pot-dashboard__summary-card">
                                    <span>{t('pot_bonus_total')}</span>
                                    <strong>{t('currency_symbol')} {convertDoubleToStringPTBR(bonusTotal)}</strong>
                                </div>
                            )}
                            <div className="pot-dashboard__summary-card">
                                <span>{t('employee_commission')}</span>
                                <strong>{t('currency_symbol')} {convertDoubleToStringPTBR(data?.employeeCommission || 0)}</strong>
                            </div>
                            <div className="pot-dashboard__summary-card">
                                <span>{t('gamification.pot_total_points')}</span>
                                <strong>{data?.totalPoints || 0}</strong>
                            </div>
                            <div className="pot-dashboard__summary-card">
                                <span>{t('gamification.pot_participants')}</span>
                                <strong>{data?.employeesWithPoints || 0}/{data?.totalEmployees || 0}</strong>
                            </div>
                        </div>

                        {hasDistribution ? (
                            <div className="table-responsive">
                                <table className="table table-sm mb-0 pot-dashboard__ranking-table">
                                    <thead>
                                        <tr>
                                            <th className="align-middle">{t('position')}</th>
                                            <th className="align-middle">{t('employee')}</th>
                                            <th className="align-middle">{t('gamification.score')}</th>
                                            <th className="align-middle">{t('proportion')}</th>
                                            {hasBonus && <th className="align-middle text-end">{t('pot_bonus_percent')}</th>}
                                            <th className="align-middle text-end">{t('gamification.reward')}</th>
                                            <th className="align-middle text-end">{t('actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.distribution.map((item, index) => {
                                            const hasScoreAdjustment = !!item.scoreAdjustmentId;
                                            return (
                                            <tr key={`${item.employee}-${index}`}>
                                                <td className="pot-dashboard__ranking-position">
                                                    {index === 0 ? <span className="fa fa-trophy"></span> : `${index + 1}º`}
                                                </td>
                                                <td><strong>{item.employee}</strong></td>
                                                <td>
                                                    {hasScoreAdjustment && (
                                                        <small className="text-muted text-decoration-line-through me-1">
                                                            {item.originalPoints}
                                                        </small>
                                                    )}
                                                    <span className={hasScoreAdjustment ? "text-warning fw-bold" : ""}>
                                                        {item.points}
                                                        {hasScoreAdjustment && (
                                                            <i className="fas fa-pen-to-square fa-xs ms-1"
                                                                title={t('gamification.score_adjusted')}></i>
                                                        )}
                                                    </span>
                                                </td>
                                                <td>{convertDoubleToStringPTBR(item.proportion)}%</td>
                                                {hasBonus && (
                                                    <td className="text-end">
                                                        {Number(item.bonus || 0) > 0 ? (
                                                            <>
                                                                {t('currency_symbol')} {convertDoubleToStringPTBR(item.bonus)}
                                                                {/* O percentual ao lado responde a primeira pergunta
                                                                    de quem lê a linha: de onde saiu esse valor. */}
                                                                <small className="text-muted d-block">
                                                                    {convertDoubleToStringPTBR(item.bonusPercent || 0)}%
                                                                </small>
                                                            </>
                                                        ) : '-'}
                                                    </td>
                                                )}
                                                {/* A recompensa é rateio mais adicional: é o que a pessoa recebe.
                                                    O fallback cobre a publicação gradual, quando o backend antigo
                                                    ainda não manda o total pronto. */}
                                                <td className="text-end">
                                                    <strong>{t('currency_symbol')} {convertDoubleToStringPTBR(item.totalReward ?? item.reward)}</strong>
                                                </td>
                                                <td className="text-end text-nowrap">
                                                    <button
                                                        className="btn btn-link btn-sm p-0 text-primary"
                                                        onClick={() => openScoreModal(item)}
                                                        title={hasScoreAdjustment ? t('gamification.score_edit_adjustment') : t('gamification.score_adjust')}
                                                    >
                                                        <i className="fas fa-sliders"></i>
                                                    </button>
                                                    {hasScoreAdjustment && (
                                                        <button
                                                            className="btn btn-link btn-sm p-0 text-danger ms-2"
                                                            onClick={() => handleRemoveScoreAdjustment(item)}
                                                            title={t('gamification.score_remove_adjustment')}
                                                        >
                                                            <i className="fas fa-rotate-left"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <Alert variant="light" className="pot-dashboard__empty-state pot-dashboard__empty-state--inner">
                                {t('gamification.pot_no_points_hint')}
                            </Alert>
                        )}

                        {hasDistribution && (
                            <p className="pot-dashboard__summary-footnote">
                                {t('gamification.pot_distribution_footnote')}
                            </p>
                        )}

                        {hasDistribution && (data?.totalEmployees - data?.employeesWithPoints) > 0 && (
                            <p className="pot-dashboard__summary-footnote text-warning mt-1">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                {t('gamification.pot_abstained', { count: data.totalEmployees - data.employeesWithPoints })}
                            </p>
                        )}
                    </>
                )}
            </div>

            <ModalPageEvent
                title={t('gamification.pot_adjust_value')}
                show={showModal}
                setShow={setShowModal}
                size="md"
                centered={true}
                btnOnClick={handleSaveAdjustment}
                btnText={saving ? t('saving') : t('save')}
                btnDisabled={saving}
                child={
                    <>
                        {hasAdjustment && (
                            <Alert variant="info" className="py-2 mb-3">
                                <small>
                                    <strong>{t('gamification.pot_original_value')}:</strong>{" "}
                                    {t('currency_symbol')} {convertDoubleToStringPTBR(data?.potTotalOriginal || 0)}
                                </small>
                            </Alert>
                        )}
                        <Row className="g-3">
                            <Col md={12}>
                                <FormGroup>
                                    <FormLabel>{t('gamification.pot_adjusted_value')}</FormLabel>
                                    <InputMoney
                                        currency
                                        value={adjustedValue}
                                        onChange={(_, meta) => setAdjustedValue(meta.floatValue ?? null)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <FormLabel>{t('reason')} <span className="text-muted">({t('optional')})</span></FormLabel>
                                    <TextArea
                                        value={reason}
                                        onChange={e => setReason(e.target.value)}
                                        placeholder={t('gamification.pot_adjustment_reason_placeholder')}
                                        minRows={2}
                                        maxLength={500}
                                        counter
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </>
                }
            />

            <ModalPageEvent
                title={t('gamification.score_adjust')}
                show={showScoreModal}
                setShow={setShowScoreModal}
                size="md"
                centered={true}
                btnOnClick={handleSaveScoreAdjustment}
                btnText={savingScore ? t('saving') : t('save')}
                btnDisabled={savingScore}
                child={
                    <>
                        <Alert variant="info" className="py-2 mb-3">
                            <small>
                                <strong>{scoreEmployee?.employee}</strong> —{" "}
                                {t('gamification.score_original_points')}: {scoreEmployee?.originalPoints ?? 0}
                            </small>
                        </Alert>
                        <Row className="g-3">
                            <Col md={12}>
                                <FormGroup>
                                    <FormLabel>{t('gamification.score_adjusted_points')}</FormLabel>
                                    <FormControl
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={adjustedPoints}
                                        onChange={e => setAdjustedPoints(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <FormLabel>{t('reason')} <span className="text-muted">({t('optional')})</span></FormLabel>
                                    <TextArea
                                        value={scoreReason}
                                        onChange={e => setScoreReason(e.target.value)}
                                        placeholder={t('gamification.score_adjustment_reason_placeholder')}
                                        minRows={2}
                                        maxLength={500}
                                        counter
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </>
                }
            />
        </>
    );
};

export default DistributionPot;
